import { useGT } from 'gt-react';
import { Button } from '@/components/ui/button';
import { DateRangePicker } from '@/components/common/date-pickers/date-range-picker';
import {
  fromDayPickerRange,
  resolvePreset,
  toDayPickerRange,
  type DateOnlyRange,
  type PresetKey,
} from './dateRangePresets';

/**
 * A date filter for list pages: a range calendar plus quick presets. The
 * active preset is derived from the current range (not stored), so a
 * URL-restored range still highlights its preset.
 */
export function DateRangePresetFilter({
  value,
  onChange,
  placeholder,
  className,
}: {
  value: DateOnlyRange;
  onChange: (range: DateOnlyRange) => void;
  /** What the range is on, shown while nothing is picked (e.g. "Received date"). */
  placeholder: string;
  className?: string;
}) {
  const gt = useGT();
  const context = 'date range filter quick preset button';
  const presets: { key: PresetKey; label: string }[] = [
    { key: 'thisWeek', label: gt('This Week', { context }) },
    { key: 'thisMonth', label: gt('This Month', { context }) },
    { key: 'lastMonth', label: gt('Last Month', { context }) },
    { key: 'thisYear', label: gt('This Year', { context }) },
    { key: 'all', label: gt('All', { context }) },
  ];
  const isActive = (key: PresetKey) => {
    const range = resolvePreset(key);
    return range.from === value.from && range.to === value.to;
  };

  return (
    <div className={className ?? 'flex flex-wrap items-center gap-2'}>
      <DateRangePicker
        value={toDayPickerRange(value)}
        onChange={(range) => onChange(fromDayPickerRange(range))}
        clearable
        placeholder={placeholder}
        className="w-[240px]"
      />
      <div className="flex flex-wrap items-center gap-1">
        {presets.map((preset) => (
          <Button
            key={preset.key}
            type="button"
            size="sm"
            variant={isActive(preset.key) ? 'secondary' : 'ghost'}
            aria-pressed={isActive(preset.key)}
            onClick={() => onChange(resolvePreset(preset.key))}
          >
            {preset.label}
          </Button>
        ))}
      </div>
    </div>
  );
}
